export interface ApiGatewayKeyRecordResponse {
  id: string;
  tenant_id: string;
  service_account_id?: string | null;
  display_name: string;
  prefix: string;
  status: string;
  scopes: string[];
  permissions: string[];
  allowed_ips: string[];
  linked_principal_status?: string | null;
  created_at: string;
  last_used_at?: string | null;
  expires_at?: string | null;
}

export interface ApiGatewayListResponse {
  keys?: ApiGatewayKeyRecordResponse[];
}

export interface ApiGatewayCreatePayload {
  display_name: string;
  service_account_id?: string | null;
  permissions?: string[];
  scopes: string[];
  allowed_ips: string[];
  expires_at?: string | null;
  metadata?: Record<string, unknown>;
}

export interface ApiGatewayCreateResponse {
  key: ApiGatewayKeyRecordResponse;
  secret: string;
}

export interface ApiGatewayPermissionFamily {
  id: string;
  label: string;
  description: string;
  read_permission: string;
  manage_permission: string;
  path_prefixes: string[];
}

export interface ApiGatewayPermissionFamilyListResponse {
  families?: ApiGatewayPermissionFamily[];
}

async function requestJSON<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {})
    },
    ...init
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(message || `Request failed: ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function listApiGatewayKeys(): Promise<ApiGatewayKeyRecordResponse[]> {
  const payload = await requestJSON<ApiGatewayListResponse>('/api/v1/api-keys');
  return payload.keys ?? [];
}

export async function createApiGatewayKey(payload: ApiGatewayCreatePayload): Promise<ApiGatewayCreateResponse> {
  return requestJSON<ApiGatewayCreateResponse>('/api/v1/api-keys', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
}

export async function listApiGatewayPermissionFamilies(): Promise<ApiGatewayPermissionFamily[]> {
  const payload = await requestJSON<ApiGatewayPermissionFamilyListResponse>('/api/v1/api-keys/permissions');
  return payload.families ?? [];
}

export async function revokeApiGatewayKey(keyId: string): Promise<void> {
  await requestJSON<void>(`/api/v1/api-keys/${encodeURIComponent(keyId)}`, {
    method: 'DELETE'
  });
}

export async function rotateApiGatewayKey(keyId: string): Promise<ApiGatewayCreateResponse> {
  return requestJSON<ApiGatewayCreateResponse>(`/api/v1/api-keys/${encodeURIComponent(keyId)}/rotate`, {
    method: 'POST'
  });
}

export function getApiGatewayOpenApiJsonUrl(): string {
  return '/api/v1/api-gateway/openapi.json';
}

export function getApiGatewayOpenApiDocsUrl(): string {
  return '/api-gateway/console';
}
